// @file Surface language panel store
import { trackEvent } from '@@/bits/analytics'
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import device from '@@/bits/device'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { p__, __ } from '@@/bits/intl'
import { areLocalesEquivalent, availableLocales, getLocaleName, isRealLocale } from '@@/bits/locale'
import { clearSearchParam, getSearchParam, setSearchParam, transformUrl } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { OzMenuRowsRowRole, type OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import {
  postTranslationState,
  showTranslationErrorNotification,
  showTranslationSuccessNotification,
} from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { WallLanguageDetectionApi } from '@@/surface/api/wall_language_detection'
import type { Id, TranslatedComment, TranslatedSection, TranslatedWall, TranslatedWish } from '@@/types'
import type { CommentId } from '@@/types/surface_comments'
import type { RootState } from '@@/vuexstore/surface/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const TRANSLATION_QUERY_PARAM_KEY = 'lang'
const ORIGINAL_LANGUAGE_KEY = 'original'
const ANOTHER_LANGUAGE_KEY = 'anotherLanguage'

enum TranslationStates {
  Disabled = 'disabled',
  Initial = 'initial',
  Loading = 'loading',
  Done = 'done',
}

enum TranslationSource {
  PANEL = 'panel',
  BANNER = 'banner',
  CHAT = 'chat',
}

enum EntityToTranslate {
  WALL = 'wall',
  SECTION = 'section',
  COMMENT = 'comment',
  WISH = 'wish',
}

const userLocale = getHyphenatedCurrentLocale()

const useSurfaceTranslationsStore = defineStore('surfaceTranslationsStore', () => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  const xSurfaceLanguagePanel = ref<boolean>(false)
  const currentLanguage = ref<string>(ORIGINAL_LANGUAGE_KEY) // Holds the language code that is used to translate the padlet
  const unsavedLanguage = ref<string>(currentLanguage.value) // Temporary storage for the language code that the user has selected to translate the padlet before saving it
  const detectedLanguage = ref<string>('') // Holds the language code that is detected from the padlet
  const detectedLanguages = ref<string[]>([]) // Holds the list of languages detected from the padlet
  const isTranslatingPadletFromPanel = ref<boolean>(false)
  const isTranslatingPadletFromBanner = ref<boolean>(false)
  const isTranslatingPadletFromChat = ref<boolean>(false)
  const isDetectingLanguage = ref<boolean>(false)
  const commentIdsBeingTranslated = ref<Set<CommentId>>(new Set())
  const wishIdsBeingTranslated = ref<Set<Id>>(new Set())

  const isViewingTranslatedPadlet = computed<boolean>(() => !isOriginalLanguage(currentLanguage.value))
  const isMultipleLanguagesDetected = computed<boolean>(() => detectedLanguage.value === 'multiple')

  const userLocaleDisplayName = computed<string>(() => {
    return getLocaleName(userLocale)
  })
  const isUserLocaleDetected = computed<boolean>(
    () =>
      (detectedLanguages.value && detectedLanguages.value.some((lang) => areLocalesEquivalent(lang, userLocale))) ||
      detectedLanguage.value === userLocale,
  )

  // Presentable name for current language with custom logic for original language
  const currentLanguageDisplayName = computed<string>(() => {
    if (isOriginalLanguage(currentLanguage.value)) {
      return p__(
        'This text appears in the language selector panel. It shows the original language of the content. localeName is the name of the language that we have detected the content to be in.',
        'Original ({localeName})',
        { localeName: detectedLanguageDisplayName.value },
      )
    }
    return availableLocales.find((locale) => locale.html === currentLanguage.value)?.name ?? ''
  })

  const detectedLanguageDisplayName = computed<string>(() => {
    const displayName = availableLocales.find((locale) => locale.html === detectedLanguage.value)?.name
    if (detectedLanguage.value === 'multiple') {
      return p__('Indicates that the text is in multiple languages', 'multiple languages')
    } else if (detectedLanguage.value === 'unknown' || displayName == null) {
      return p__('Indicates that the text is in a language that we could not detect', 'Unknown')
    } else if (
      isAppUsing('translatePadletV2') &&
      (detectedLanguage.value === 'en' || detectedLanguage.value === 'en-GB')
    ) {
      return __('English')
    } else if (detectedLanguage.value === 'en') {
      return __('English - US')
    }
    return displayName
  })

  const showLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = true
  }

  const closeLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = false
    // Reset unsaved language to current language
    unsavedLanguage.value = currentLanguage.value
  }

  const translatedWall = ref<TranslatedWall | null>(null)

  const isCommentTranslated = (id: CommentId): boolean => {
    const comment = translatedWall.value?.comments.find((comment) => comment.commentId === id.toString())
    return comment?.detectedLanguage != null && comment?.detectedLanguage !== currentLanguage.value
  }

  const isWishTranslated = (id: number | undefined): boolean => {
    if (id == null) return false
    const wish = translatedWall.value?.wishes.find((wish) => wish.wishId === id.toString())
    return wish?.detectedLanguage != null && wish?.detectedLanguage !== currentLanguage.value
  }

  const isWishTranslatedOrBeingTranslated = (id: number | undefined): boolean => {
    if (id == null) return false
    return (
      isViewingTranslatedPadlet.value &&
      (wishIdsBeingTranslated.value.has(id) || (isWishTranslated(id) && isMultipleLanguagesDetected.value))
    )
  }

  function postTranslationStateToNativeApp(translationState: TranslationStates): void {
    postMessage(
      postTranslationState({
        translationState,
        detectedLanguageDisplayName: detectedLanguageDisplayName.value,
        currentLanguage: currentLanguage.value,
        currentLanguageDisplayName: currentLanguageDisplayName.value,
        isMultipleLanguagesDetected: isMultipleLanguagesDetected.value,
        translatedWall: {
          title: translatedWall.value?.title ?? null,
          description: translatedWall.value?.description ?? null,
        },
      }),
    )
  }

  const previouslyTranslatedLanguage = ref<string | null>(null)

  async function handleTranslation(selectedLanguage: string, source: TranslationSource): Promise<void> {
    if (device.app) postTranslationStateToNativeApp(TranslationStates.Loading)

    try {
      // Get previously translated language if any, before we clear the lang param
      previouslyTranslatedLanguage.value = getSearchParam(TRANSLATION_QUERY_PARAM_KEY)

      if (!isOriginalLanguage(selectedLanguage)) {
        setSearchParam(TRANSLATION_QUERY_PARAM_KEY, selectedLanguage)
        // Ensure that the banner is displayed, especially in the event where the user has dismissed it and then translated the padlet
        if (!isAppUsing('translatePadletV2')) {
          unDismissTranslationBanner()
        }
      } else {
        clearSearchParam(TRANSLATION_QUERY_PARAM_KEY)
      }

      if (source === TranslationSource.PANEL) {
        isTranslatingPadletFromPanel.value = true
      } else if (source === TranslationSource.BANNER) {
        isTranslatingPadletFromBanner.value = true
      } else {
        isTranslatingPadletFromChat.value = true
      }

      // Handle "auto" language code
      let translatedLanguage = selectedLanguage
      if (selectedLanguage === 'auto') {
        translatedLanguage = userLocale
      }

      const { WallTranslationApi } = await import('@@/surface/api/wall_translation')
      translatedWall.value = await WallTranslationApi.translate(surfaceStore.wallId, translatedLanguage)
      currentLanguage.value = translatedLanguage

      if (device.app) {
        postTranslationStateToNativeApp(
          isOriginalLanguage(selectedLanguage) ? TranslationStates.Initial : TranslationStates.Done,
        )
      }

      // Early return if translation happens on page load to not show the snackbar
      if (!isAppUsing('translatePadletV2') || previouslyTranslatedLanguage.value === selectedLanguage) return

      const undoLanguage = previouslyTranslatedLanguage.value ?? ORIGINAL_LANGUAGE_KEY
      displayTranslationSuccessNotification(undoLanguage)
    } catch (e) {
      if (device.app) postTranslationStateToNativeApp(TranslationStates.Initial)
      displayTranslationErrorNotification(selectedLanguage, source)
      clearSearchParam(TRANSLATION_QUERY_PARAM_KEY)
      dismissTranslationBanner()
      captureFetchException(e, { source: 'SurfaceTranslatePadletToLanguage' })
    } finally {
      isTranslatingPadletFromPanel.value = false
      isTranslatingPadletFromBanner.value = false
      isTranslatingPadletFromChat.value = false
      // We don't want to collapse the banner immediately after the translation happens on page load
      // We want to let it collapse after 8s timeout handled in SurfaceTranslationBanner
      if (isAppUsing('translatePadletV2') && previouslyTranslatedLanguage.value !== selectedLanguage) {
        collapseBanner()
      }
      closeLanguagePanel()
    }
  }

  async function translatePadletFromLanguagePanel(selectedLanguage: string): Promise<void> {
    if (isTranslatingPadletFromBanner.value || isTranslatingPadletFromPanel.value || isTranslatingPadletFromChat.value)
      return
    trackEvent('Surface', 'Translated padlet from language panel', null, null, { language: selectedLanguage })
    await handleTranslation(selectedLanguage, TranslationSource.PANEL)
  }

  async function translatePadletFromBanner(selectedLanguage: string): Promise<void> {
    if (isTranslatingPadletFromBanner.value || isTranslatingPadletFromPanel.value || isTranslatingPadletFromChat.value)
      return
    trackEvent('Surface', 'Translated padlet from language banner', null, null, { language: selectedLanguage })
    await handleTranslation(selectedLanguage, TranslationSource.BANNER)
  }

  async function translatePadletFromSurfaceAiChat(selectedLanguage: string): Promise<void> {
    if (isTranslatingPadletFromBanner.value || isTranslatingPadletFromPanel.value || isTranslatingPadletFromChat.value)
      return
    trackEvent('Surface', 'Translated padlet from surface AI chat', null, null, { language: selectedLanguage })
    await handleTranslation(selectedLanguage, TranslationSource.CHAT)
  }

  async function translateWallEntity(): Promise<void> {
    try {
      const { WallTranslationApi } = await import('@@/surface/api/wall_translation')
      const translatedEntity = await WallTranslationApi.translateEntity(
        surfaceStore.wallId,
        EntityToTranslate.WALL,
        currentLanguage.value,
      )

      if (translatedWall.value != null && translatedEntity?.translation != null) {
        translatedWall.value.title =
          (translatedEntity.translation as Partial<TranslatedWall>).title ?? translatedWall.value.title
        translatedWall.value.description =
          (translatedEntity.translation as Partial<TranslatedWall>).description ?? translatedWall.value.description
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceTranslateWallEntity' })
    }
  }

  async function translateCustomPostProperties(): Promise<void> {
    try {
      const { WallTranslationApi } = await import('@@/surface/api/wall_translation')
      const translatedEntity = await WallTranslationApi.translateEntity(
        surfaceStore.wallId,
        EntityToTranslate.WALL,
        currentLanguage.value,
      )

      if (translatedWall.value != null && translatedEntity?.translation != null) {
        translatedWall.value.customPostProperties =
          (translatedEntity.translation as Partial<TranslatedWall>).customPostProperties ??
          translatedWall.value.customPostProperties
        translatedWall.value.selectionOptions =
          (translatedEntity.translation as Partial<TranslatedWall>).selectionOptions ??
          translatedWall.value.selectionOptions
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceTranslateCustomPostProperties' })
    }
  }

  async function translateSectionEntity(sectionId: number): Promise<void> {
    try {
      const { WallTranslationApi } = await import('@@/surface/api/wall_translation')
      const translatedEntity = await WallTranslationApi.translateEntity(
        surfaceStore.wallId,
        EntityToTranslate.SECTION,
        currentLanguage.value,
        sectionId.toString(),
      )

      if (translatedWall.value != null && translatedEntity?.translation != null) {
        const translatedSectionData = translatedEntity.translation as TranslatedSection
        const existingSectionIndex = translatedWall.value.sections.findIndex(
          (section) => section.sectionId === sectionId.toString(),
        )

        if (existingSectionIndex !== -1) {
          // Section exists, update its title
          translatedWall.value.sections[existingSectionIndex].sectionTitle = translatedSectionData.sectionTitle
        } else {
          // Section is new, add it to the translated wall sections array
          translatedWall.value.sections.push({
            sectionId: sectionId.toString(),
            sectionTitle: translatedSectionData.sectionTitle,
          })
        }
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceTranslateSectionEntity' })
    }
  }

  function deleteSectionFromTranslatedWall(sectionId: number): void {
    if (translatedWall.value == null) return
    translatedWall.value.sections = translatedWall.value.sections.filter((s) => s.sectionId !== sectionId.toString())
  }

  async function translateCommentEntity(commentId: CommentId): Promise<void> {
    commentIdsBeingTranslated.value.add(commentId)
    try {
      const { WallTranslationApi } = await import('@@/surface/api/wall_translation')
      const translatedEntity = await WallTranslationApi.translateEntity(
        surfaceStore.wallId,
        EntityToTranslate.COMMENT,
        currentLanguage.value,
        commentId.toString(),
      )
      if (translatedWall.value != null && translatedEntity?.translation != null) {
        const translatedCommentData = translatedEntity.translation as TranslatedComment
        const existingCommentIndex = translatedWall.value.comments.findIndex(
          (c) => c.commentId === commentId.toString(),
        )

        if (existingCommentIndex !== -1) {
          translatedWall.value.comments[existingCommentIndex].htmlBody = translatedCommentData.htmlBody
        } else {
          translatedWall.value.comments.push({
            commentId: commentId.toString(),
            htmlBody: translatedCommentData.htmlBody,
            wishId: translatedCommentData.wishId,
            detectedLanguage: translatedCommentData.detectedLanguage,
          })
        }
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceTranslateCommentEntity' })
    } finally {
      commentIdsBeingTranslated.value.delete(commentId)
    }
  }

  function deleteCommentFromTranslatedWall(commentId: CommentId): void {
    if (translatedWall.value == null) return
    translatedWall.value.comments = translatedWall.value.comments.filter((c) => c.commentId !== commentId.toString())
  }

  async function translateWishEntity(wishId: number): Promise<void> {
    try {
      wishIdsBeingTranslated.value.add(wishId)
      const { WallTranslationApi } = await import('@@/surface/api/wall_translation')
      const translatedEntity = await WallTranslationApi.translateEntity(
        surfaceStore.wallId,
        EntityToTranslate.WISH,
        currentLanguage.value,
        wishId.toString(),
      )
      if (translatedWall.value != null && translatedEntity?.translation != null) {
        const translatedWishData = translatedEntity.translation as TranslatedWish
        const existingWishIndex = translatedWall.value.wishes.findIndex((w) => w.wishId === wishId.toString())

        if (existingWishIndex !== -1) {
          const existingWish = translatedWall.value.wishes[existingWishIndex]
          existingWish.subject = translatedWishData.subject
          existingWish.body = translatedWishData.body
          existingWish.attachmentCaption = translatedWishData.attachmentCaption
          existingWish.customProperties = translatedWishData.customProperties
          existingWish.attachmentProps = translatedWishData.attachmentProps
        } else {
          translatedWall.value.wishes.push({
            wishId: wishId.toString(),
            sectionId: translatedWishData.sectionId,
            subject: translatedWishData.subject,
            body: translatedWishData.body,
            attachmentCaption: translatedWishData.attachmentCaption,
            customProperties: translatedWishData.customProperties,
            attachmentProps: translatedWishData.attachmentProps,
          })
        }
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceTranslateWishEntity' })
    } finally {
      wishIdsBeingTranslated.value.delete(wishId)
    }
  }

  function deleteWishFromTranslatedWall(wishId: number): void {
    if (translatedWall.value == null) return
    translatedWall.value.wishes = translatedWall.value.wishes.filter((w) => w.wishId !== wishId.toString())
  }

  async function detectLanguage(): Promise<void> {
    try {
      isDetectingLanguage.value = true
      const data = await WallLanguageDetectionApi.detectLanguage(surfaceStore.wallId)
      detectedLanguage.value = data.language
      detectedLanguages.value = data.detected_languages ?? []

      if (device.app && xTranslateToUserLocaleBanner.value) {
        postMessage(
          postTranslationState({
            translationState: TranslationStates.Initial,
            detectedLanguageDisplayName: detectedLanguageDisplayName.value,
            currentLanguage: currentLanguage.value,
            currentLanguageDisplayName: currentLanguageDisplayName.value,
            isMultipleLanguagesDetected: isMultipleLanguagesDetected.value,
          }),
        )
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceLanguageDetection' })
    } finally {
      isDetectingLanguage.value = false
    }
  }

  const hasUnsavedLanguageChanges = computed<boolean>(() => unsavedLanguage.value !== currentLanguage.value)

  const setUnsavedLanguage = (language: string): void => {
    unsavedLanguage.value = language
  }

  /**
   * Controls whether translation UI elements should be shown in the top bar.
   * This is true when:
   * - Language detection is complete AND
   * - Either viewing a translated padlet OR the banner to translate to user locale should be shown
   *
   * This differs from `xTranslateToUserLocaleBanner` in that:
   * - `xTranslateToUserLocaleBanner` only shows when the padlet is NOT translated and user's locale differs from detected language
   * - `xTranslationInTopbar` shows for both translated padlets AND when translation is available
   * - When the topbar is not shown (mobile, embedded), this still returns true if conditions are met
   */
  const xTranslationInTopbar = computed<boolean>(
    () => !isDetectingLanguage.value && (isViewingTranslatedPadlet.value || xTranslateToUserLocaleBanner.value),
  )

  const xTranslationBanner = computed<boolean>(
    () =>
      isAppUsing('translatePadlet') &&
      (isAppUsing('translatePadletV2') || !hasDismissedTranslationBanner.value) &&
      !isDetectingLanguage.value &&
      (xRevertToOriginalBanner.value || xTranslateToUserLocaleBanner.value),
  )

  const xMobileTranslationBanner = computed<boolean>(
    () =>
      isAppUsing('translatePadlet') &&
      isAppUsing('translatePadletV2') &&
      !isDetectingLanguage.value &&
      (xRevertToOriginalBanner.value || xTranslateToUserLocaleBanner.value),
  )

  const hasDismissedTranslationBanner = ref<boolean>(false)
  const dismissTranslationBanner = (): void => {
    hasDismissedTranslationBanner.value = true
  }
  const unDismissTranslationBanner = (): void => {
    hasDismissedTranslationBanner.value = false
  }

  const isBannerExpanded = ref<boolean>(true)
  const expandBanner = (): void => {
    if (hasDismissedTranslationBanner.value) {
      isBannerExpanded.value = true
    }
  }

  const collapseBanner = (): void => {
    isBannerExpanded.value = false
    dismissTranslationBanner()
  }

  /**
   * Handles the focus event on the search bar.
   *
   * Collapses the translation banner if translatePadletV2 is enabled and the device is not using
   * the surface top bar in desktop layout. Does nothing otherwise.
   */
  const handleSearchBarFocus = (): void => {
    if (!isAppUsing('translatePadletV2')) return
    if (surfaceStore.isDesktopLayout) return

    collapseBanner()
  }

  const xRevertToOriginalBanner = computed<boolean>(() => isViewingTranslatedPadlet.value)
  const xTranslateToUserLocaleBanner = computed<boolean>(
    () =>
      !isViewingTranslatedPadlet.value &&
      !isUserLocaleDetected.value &&
      isRealLocale(userLocale) &&
      !surfaceVuexStore?.getters.amIOwner,
  )

  const decorateUrlWithTranslationParam = (url: string): string => {
    return transformUrl(url, { searchParams: { [TRANSLATION_QUERY_PARAM_KEY]: currentLanguage.value } })
  }

  const isOriginalLanguage = (language: string): boolean => {
    return language === ORIGINAL_LANGUAGE_KEY || language === detectedLanguage.value
  }

  // Helper function to create a standard menu row
  const createMenuRow = ({
    eventPayload,
    text,
    testId,
    isSelected,
    ref,
    rightIcon,
    rightIconSize,
  }: {
    eventPayload: string
    text: string
    testId: string
    isSelected: boolean
    ref?: string
    rightIcon?: string
    rightIconSize?: number
  }): OzMenuRowsRow => ({
    rightIcon: rightIcon ?? (isSelected ? 'checkmark_thicker' : ''),
    rightIconHighlighted: true,
    role: OzMenuRowsRowRole.MenuItemRadio,
    ariaChecked: String(isSelected),
    eventPayload,
    ref,
    testId,
    textStyleClass: isSelected ? 'font-semibold text-body' : 'text-body',
    text,
    ...(rightIconSize && { rightIconSize }),
  })

  // Helper function to create the original language row
  const createOriginalLanguageRow = (selectedLanguage: string): OzMenuRowsRow => {
    return createMenuRow({
      eventPayload: ORIGINAL_LANGUAGE_KEY,
      text: __('Original (%{detectedLocale})', { detectedLocale: detectedLanguageDisplayName.value }),
      testId: 'originalLanguageMenuRow',
      isSelected: isOriginalLanguage(selectedLanguage),
    })
  }

  // Helper function to create a locale row
  const createLocaleRow = (locale: { html: string; name: string }, selectedLanguage: string): OzMenuRowsRow => {
    return createMenuRow({
      eventPayload: locale.html,
      text: locale.name,
      testId: `${locale.html}LanguageMenuRow`,
      isSelected: selectedLanguage === locale.html,
      ref: locale.html,
    })
  }

  // Helper function to create the "Another language" row
  const createAnotherLanguageRow = (): OzMenuRowsRow => {
    return createMenuRow({
      eventPayload: ANOTHER_LANGUAGE_KEY,
      text: __('Another language'),
      testId: 'anotherLanguageMenuRow',
      isSelected: false,
      ref: ANOTHER_LANGUAGE_KEY,
      rightIcon: 'chevron_right_center',
      rightIconSize: 14,
    })
  }

  const isDrawerExpanded = ref<boolean>(false)

  const toggleDrawer = (): void => {
    isDrawerExpanded.value = !isDrawerExpanded.value
  }

  const displayTranslationSuccessNotification = (undoLanguage: string): void => {
    if (device.app) {
      postMessage(
        showTranslationSuccessNotification({
          currentLanguageDisplayName: currentLanguageDisplayName.value,
          undoLanguage,
          undoLanguageDisplayName: getLanguageDisplayName(undoLanguage),
        }),
      )
    } else {
      const translatedSnackbarUid = globalSnackbarStore.setSnackbar({
        message: __('Padlet translated to %{language}', { language: currentLanguageDisplayName.value }),
        notificationType: SnackbarNotificationType.success,
        timeout: 5000,
        actionText: __('Undo'),
        actionTextActions: [
          async () => {
            globalSnackbarStore.removeSnackbar(translatedSnackbarUid)
            unDismissTranslationBanner()
            // Always use the banner source when undoing a translation since undo cannot be done from the panel
            await handleTranslation(undoLanguage, TranslationSource.BANNER)
          },
        ],
      })
    }
  }

  const displayTranslationErrorNotification = (selectedLanguage: string, source: TranslationSource): void => {
    if (device.app) {
      postMessage(
        showTranslationErrorNotification({
          selectedLanguage,
          selectedLanguageDisplayName: getLanguageDisplayName(selectedLanguage),
        }),
      )
    } else {
      const translationFailedSnackbarUid = globalSnackbarStore.setSnackbar({
        message: __('Padlet could not be translated'),
        notificationType: SnackbarNotificationType.error,
        timeout: 5000,
        actionText: __('Retry'),
        actionTextActions: [
          async () => {
            globalSnackbarStore.removeSnackbar(translationFailedSnackbarUid)
            await handleTranslation(selectedLanguage, source)
          },
        ],
      })
    }
  }

  const getLanguageDisplayName = (language: string): string => {
    if (isOriginalLanguage(language)) {
      return p__(
        'This text appears in the language selector panel. It shows the original language of the content. localeName is the name of the language that we have detected the content to be in.',
        'Original ({localeName})',
        { localeName: detectedLanguageDisplayName.value },
      )
    }
    return availableLocales.find((locale) => locale.html === language)?.name ?? ''
  }

  return {
    isDetectingLanguage,
    xSurfaceLanguagePanel,
    currentLanguage,
    detectedLanguage,
    unsavedLanguage,
    isTranslatingPadletFromPanel,
    isTranslatingPadletFromBanner,
    showLanguagePanel,
    closeLanguagePanel,
    translatePadletFromLanguagePanel,
    translatePadletFromBanner,
    translatePadletFromSurfaceAiChat,
    detectLanguage,
    hasUnsavedLanguageChanges,
    setUnsavedLanguage,
    currentLanguageDisplayName,
    isViewingTranslatedPadlet,
    hasDismissedTranslationBanner,
    unDismissTranslationBanner,
    dismissTranslationBanner,
    isBannerExpanded,
    expandBanner,
    collapseBanner,
    handleSearchBarFocus,
    xRevertToOriginalBanner,
    xTranslateToUserLocaleBanner,
    xTranslationInTopbar,
    userLocaleDisplayName,
    translatedWall,
    detectedLanguageDisplayName,
    isCommentTranslated,
    isMultipleLanguagesDetected,
    isWishTranslated,
    translateWallEntity,
    translateSectionEntity,
    deleteSectionFromTranslatedWall,
    translateCommentEntity,
    deleteCommentFromTranslatedWall,
    translateWishEntity,
    deleteWishFromTranslatedWall,
    translateCustomPostProperties,
    decorateUrlWithTranslationParam,
    commentIdsBeingTranslated,
    wishIdsBeingTranslated,
    isWishTranslatedOrBeingTranslated,
    isOriginalLanguage,
    createOriginalLanguageRow,
    createLocaleRow,
    createAnotherLanguageRow,
    xTranslationBanner,
    xMobileTranslationBanner,
    isDrawerExpanded,
    toggleDrawer,
  }
})
export {
  ANOTHER_LANGUAGE_KEY,
  ORIGINAL_LANGUAGE_KEY,
  TRANSLATION_QUERY_PARAM_KEY,
  TranslationStates,
  useSurfaceTranslationsStore,
}
